import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as signalR from '@microsoft/signalr';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import {
  restoreLangForUser,
  saveLangSnapshot,
  writeLangForUser,
} from 'app/helpers/uiLanguage';
import {
  AuthProps,
  AuthRepository,
  getStoredToken,
  getUser,
} from 'app/state/auth.repository';
import { AuthService } from 'app/state/auth.service';
import { LocalStorageService } from './local-storage-service';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from 'environments/environment';
import { NotificationsService } from 'app/state/notifications.service';
import { NotificationsRepository } from 'app/state/notifications.repository';
import { CoachMenteeChatService } from './coachMenteeChat.service';
import { GamificationService } from './gamification.service';
import { AIRoleplayService } from 'app/state/airoleplay.service';
import {
  AchievementUnlock,
  ChallengeComplete,
  GamificationUpdate,
} from '../models/gamification/gamification.dtos';

@Injectable({ providedIn: 'root' })
export class TenantSyncService {
  private connection: signalR.HubConnection;

  public connect(): Promise<void> {
    if (this.connection.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }

    return this.connection.start().catch((err) => {
      console.error('SignalR connection failed:', err);
      throw err;
    });
  }

  constructor(
    private auth: AuthService,
    public generalDataRepo: GeneralDataRepository,
    public router: Router,
    private storage: LocalStorageService,
    private authRepo: AuthRepository,
    private notificationsService: NotificationsService,
    private notificationsRepo: NotificationsRepository,
    private translocoService: TranslocoService,
    private chatService: CoachMenteeChatService,
    private gamificationService: GamificationService,
    private aiRoleplayService: AIRoleplayService,
  ) {
    const user = getStoredToken();

    let url = '/tenantHub';
    if (environment.production) {
      url = '/core/tenantHub';
    }
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => user ?? '',
      })
      .withAutomaticReconnect()
      .build();

    this.connection.on('TenantChanged', (newTenantId: string) => {
      // Compare against THIS tab's state: the localStorage key is shared by all
      // tabs and has already been updated by the tab that switched, so a
      // background tab guarded on it would keep its old token and stores while
      // its requests are already scoped to the new tenant.
      const currentTenantId = storage.getTenantId();
      if (currentTenantId === newTenantId) {
        return;
      }

      const catchUpToServer = () => {
        if (newTenantId) {
          localStorage.setItem('activeTenantId', newTenantId);
        } else {
          localStorage.removeItem('activeTenantId');
        }
        storage.setTenantId(newTenantId);
        this.router.navigate(['/home']).then(() => window.location.reload());
      };

      const token = getStoredToken();
      if (!token) {
        console.error('Cannot switch tenant: no auth token available');
        return;
      }
      this.auth.setActiveTenant(token, newTenantId).subscribe({
        next: () => {
          catchUpToServer();
        },
        error: (err) => {
          // Background sync pushed from the server (another tab/device switched
          // tenant) — the user took no action here, so log only. No toast: a
          // "please try again" prompt would be misleading with nothing to retry.
          console.error('Failed to confirm tenant switch', err);
          catchUpToServer();
        },
      });
    });

    this.connection.on(
      'ImpersonateStarted',
      (newUserId: string, newTenantId: string) => {
        const user = getUser();
        if (user?.id === newUserId) {
          return;
        }

        this.auth.impersonate(newUserId!, newTenantId).subscribe({
          next: (responce) => {
            if (responce.user) {
              const lang = responce.user.language || 'en';
              localStorage.setItem('isImpersonatingMode', 'true');
              this.authRepo.setUser(responce.user);
              saveLangSnapshot();
              writeLangForUser(responce.user.id, lang);
              this.translocoService.setActiveLang(lang);
              if (newTenantId) {
                localStorage.setItem('activeTenantId', newTenantId);
              } else {
                localStorage.removeItem('activeTenantId');
              }
            }
            this.authRepo.setToken(responce.token);
            window.location.reload();
          },
        });
      },
    );

    this.connection.on('ImpersonateStoped', (resp: AuthProps) => {
      if (resp.user) {
        restoreLangForUser(resp.user.id, resp.user.language || 'en');
      }
      this.auth.unimpersonateWithoutRequest(resp);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    });

    this.connection.on('ReloadNotifications', (resp: any) => {
      this.notificationsService.loadAllCount().subscribe((x) => {
        this.notificationsRepo.notificationCount$.next(x);
      });
    });

    this.connection.on('ReloadMessages', (resp: any) => {
      this.chatService.getAllMessagesForUser().subscribe((chats) => {
        const allMessages = chats.flatMap((c) => c.messages);
        this.generalDataRepo.updateMessages(allMessages);
      });
    });
    this.connection.on('ReloadGroupMessages', (resp: any) => {
      this.chatService.getAllMessagesForUserGroup().subscribe((chats) => {
        const allMessages = chats.flatMap((c) => c.messages);
        this.generalDataRepo.updateGroupMessages(allMessages);
      });
    });
    this.connection.on('ReloadSidebar', (resp: any) => {
      this.generalDataRepo.updateAllGeneralData(true);
    });
    this.connection.on('GamificationUpdated', (update: GamificationUpdate) => {
      this.gamificationService.updates$.next(update);
    });
    this.connection.on('AchievementUnlocked', (unlock: AchievementUnlock) => {
      this.gamificationService.achievementUnlocked$.next(unlock);
    });
    this.connection.on('ChallengeCompleted', (complete: ChallengeComplete) => {
      this.gamificationService.challengeCompleted$.next(complete);
    });
    this.connection.on(
      'RoleplayCoachingUpdated',
      (update: { roleplayId: string }) => {
        this.aiRoleplayService.coachingUpdated$.next(update);
      },
    );
    this.connection.on('LogOut', (resp: any) => {
      localStorage.clear();
      localStorage.setItem('isImpersonatingMode', 'false');
      this.router.navigate(['/signin']);
      // this.generalDataRepo.updateAllGeneralData(true);
    });

    this.connection
      .start()
      .catch((err) => console.error('SignalR error:', err));
  }
}
